/**
 * Publishes `--keyboard-inset-web`: how many CSS px of the bottom of the viewport the
 * on-screen keyboard currently covers.
 *
 * The problem it solves: every text input in this app lives in a bottom sheet (Plan's
 * From/To, Routes' search, the alerts line search), and a sheet is pinned to the bottom
 * edge — exactly where the IME opens. When nothing tells the layout the keyboard is
 * there, the field the rider is typing into is behind it.
 *
 * `visualViewport` is the only cross-platform way to measure that from the web layer: it
 * is the part of the layout viewport actually on screen, so `innerHeight` minus its
 * height and scroll offset IS the covered strip. It reports a keyboard on iOS and in
 * every mobile browser, and on Android wherever the window resizes for the IME. Where it
 * does not — an edge-to-edge Android WebView, which keeps full height and is handed the
 * keyboard as a WindowInset instead — MainActivity writes `--keyboard-inset-native` and
 * the `max()` in index.css picks the live one. See that comment for the whole contract.
 *
 * Framework-free and idempotent; called once from main.ts.
 */

/**
 * Below this the strip is not a keyboard. A browser collapsing its own URL bar, a
 * rounding difference between the two viewports, and a scroll-into-view nudge all move
 * these numbers by a few px, and lifting the sheet for any of them would make it twitch
 * while the rider scrolls. No soft keyboard is anywhere near this short.
 */
const MIN_KEYBOARD_PX = 60

/** Pinch-zoom shrinks the visual viewport too, and that is not a keyboard. */
const MAX_UNZOOMED_SCALE = 1.01

const PROPERTY = '--keyboard-inset-web'

export function measureKeyboardInset(
  viewport: Pick<VisualViewport, 'height' | 'offsetTop' | 'scale'>,
  windowHeight: number,
): number {
  if (viewport.scale > MAX_UNZOOMED_SCALE) return 0
  // offsetTop is how far the visual viewport has been scrolled down inside the layout
  // one — which is how iOS makes room for the keyboard — so it is part of the strip.
  const covered = windowHeight - viewport.height - viewport.offsetTop
  return covered >= MIN_KEYBOARD_PX ? Math.round(covered) : 0
}

let started = false

/** Start watching. Returns a teardown; safe to call in a browser-less environment. */
export function watchKeyboardInset(): () => void {
  if (started || typeof window === 'undefined') return () => {}
  const viewport = window.visualViewport
  const root = document.documentElement
  if (!viewport || !root) return () => {}
  started = true

  let published = 0
  const sync = () => {
    const next = measureKeyboardInset(viewport, window.innerHeight)
    if (next === published) return
    published = next
    root.style.setProperty(PROPERTY, `${next}px`)
  }

  // `resize` is the keyboard opening and closing; `scroll` is the visual viewport being
  // panned over the layout one, which is how iOS reveals a focused field and moves
  // offsetTop out from under us without ever firing a resize.
  viewport.addEventListener('resize', sync)
  viewport.addEventListener('scroll', sync)
  sync()

  return () => {
    viewport.removeEventListener('resize', sync)
    viewport.removeEventListener('scroll', sync)
    root.style.removeProperty(PROPERTY)
    started = false
  }
}
